import { useRef, useState } from "react";
import { Alert, Card, Descriptions, Empty, Spin, Tag, Typography } from "antd";
import PageLayout from "../../../layouts/pageLayout/pageLayout";
import Button from "../../../custom/button/button";
import useGetScholarshipApplicationQuery from "../../../hooks/api/useGetScholarshipApplicationQuery";
import useGetScholarshipTemplateQuery from "../../../hooks/api/useGetScholarshipTemplateQuery";
import useUploadScholarshipFormMutation from "../../../hooks/api/useUploadScholarshipFormMutation";
import useGetAcceptanceTemplateQuery from "../../../hooks/api/useGetAcceptanceTemplateQuery";
import useUploadAcceptanceFormMutation from "../../../hooks/api/useUploadAcceptanceFormMutation";
import { formatFullDate, formattedTime } from "../../../utils/formatDate";
import styles from "./styles.module.scss";

const getStatusColor = (statusLabel?: string) => {
  const normalizedStatus = statusLabel?.toLowerCase() ?? "";

  if (normalizedStatus.includes("approve") || normalizedStatus.includes("accept")) {
    return "green";
  }

  if (normalizedStatus.includes("reject") || normalizedStatus.includes("decline")) {
    return "red";
  }

  if (normalizedStatus.includes("pending")) {
    return "gold";
  }

  return "default";
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return "N/A";
  }

  return `${formatFullDate(value)} ${formattedTime(value)}`;
};

const Scholarship = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const acceptanceFileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedAcceptanceFileName, setSelectedAcceptanceFileName] = useState("");
  const [isFormsUnlocked, setIsFormsUnlocked] = useState(false);
  const {
    scholarshipApplication,
    isLoading: isLoadingApplication,
    isError,
    error,
    refetch,
  } = useGetScholarshipApplicationQuery();
  const { downloadTemplate, isLoading: isDownloadingTemplate } =
    useGetScholarshipTemplateQuery();
  const { uploadScholarshipForm, isLoading: isUploadingScholarshipForm } =
    useUploadScholarshipFormMutation();
  const { downloadAcceptanceTemplate, isLoading: isDownloadingAcceptanceTemplate } =
    useGetAcceptanceTemplateQuery();
  const { uploadAcceptanceForm, isLoading: isUploadingAcceptanceForm } =
    useUploadAcceptanceFormMutation();

  const hasPendingScholarship =
    scholarshipApplication?.status === 0 ||
    scholarshipApplication?.statusLabel?.toLowerCase() === "pending";

  const isApproved = (() => {
    const normalized = scholarshipApplication?.statusLabel?.toLowerCase() ?? "";
    return normalized.includes("approve") || normalized.includes("accept");
  })();

  const handleUploadClick = () => {
    if (hasPendingScholarship) {
      return;
    }

    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (hasPendingScholarship) {
      event.target.value = "";
      return;
    }

    try {
      await uploadScholarshipForm({ scholarship: file });
      setSelectedFileName(file.name);
      await refetch();
    } finally {
      event.target.value = "";
    }
  };

  const handleAcceptanceUploadClick = () => {
    if (!isApproved) {
      return;
    }

    acceptanceFileInputRef.current?.click();
  };

  const handleAcceptanceFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file || !isApproved) {
      event.target.value = "";
      return;
    }

    try {
      await uploadAcceptanceForm({ scholarship: file });
      setSelectedAcceptanceFileName(file.name);
      await refetch();
    } finally {
      event.target.value = "";
    }
  };

  return (
    <PageLayout
      heading="Scholarship"
      paragraph="Download the scholarship form, upload your completed form, and track your scholarship application."
    >
      <div className={styles.page}>
        <Card className={styles.actionCard}>
          <div className={styles.actionHeader}>
            <div className={styles.actionHeaderText}>
              <Typography.Title level={4} className={styles.cardTitle}>
                Scholarship Application Form
              </Typography.Title>

              <Typography.Paragraph className={styles.cardText}>
                Download the Application template, fill it out, then upload the completed
                file for review.
              </Typography.Paragraph>
            </div>
          </div>

          <div className={styles.actions}>
            {!isFormsUnlocked && (
              <Button
                type="button"
                text="Reapply"
                onClick={() => setIsFormsUnlocked(true)}
                className={styles.actionButton}
              />
            )}
            <Button
              type="button"
              text="Download Application Form"
              isLoading={isDownloadingTemplate}
              disabled={!isFormsUnlocked || isDownloadingTemplate}
              onClick={downloadTemplate}
              className={styles.actionButton}
            />
            <input
              ref={fileInputRef}
              type="file"
              className={styles.hiddenFileInput}
              onChange={handleFileChange}
              disabled={!isFormsUnlocked || hasPendingScholarship || isUploadingScholarshipForm}
            />
            <Button
              type="button"
              text="Upload Filled Application Form"
              isLoading={isUploadingScholarshipForm}
              disabled={!isFormsUnlocked || hasPendingScholarship || isUploadingScholarshipForm}
              onClick={handleUploadClick}
              className={styles.actionButton}
              bgColor="#CE9938"
            />
          </div>

          {hasPendingScholarship && (
            <Alert
              type="info"
              showIcon
              message="Scholarship application pending"
              description="You already have a pending scholarship application. You can upload another form after this application has been reviewed."
              className={styles.alert}
            />
          )}

          {selectedFileName && (
            <Alert
              type="success"
              showIcon
              message="Upload received"
              description={`${selectedFileName} has been submitted for scholarship review.`}
              className={styles.alert}
            />
          )}
        </Card>

        <Card className={styles.detailsCard}>
          <div className={styles.detailsHeader}>
            <Typography.Title level={4} className={styles.cardTitle}>
              Scholarship Details
            </Typography.Title>
            {scholarshipApplication && (
              <Tag
                color={getStatusColor(scholarshipApplication.statusLabel)}
                className={styles.statusTag}
              >
                {scholarshipApplication.statusLabel}
              </Tag>
            )}
          </div>

          {isLoadingApplication ? (
            <div className={styles.loading}>
              <Spin size="large" />
              <Typography.Text>Fetching scholarship details...</Typography.Text>
            </div>
          ) : isError ? (
            <Alert
              type="error"
              showIcon
              message="Unable to load scholarship details"
              description={
                error instanceof Error
                  ? error.message
                  : "Please try again later."
              }
            />
          ) : scholarshipApplication ? (
            <Descriptions bordered column={1} className={styles.details}>
              <Descriptions.Item label="Application Number">
                {scholarshipApplication.applicationNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Uploaded File">
                {scholarshipApplication.originalFileName}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(scholarshipApplication.statusLabel)}>
                  {scholarshipApplication.statusLabel}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Submitted At">
                {formatDateTime(scholarshipApplication.submittedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {formatDateTime(scholarshipApplication.updatedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Admin Remarks">
                {scholarshipApplication.adminRemarks || "N/A"}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Empty
              description="No scholarship application has been submitted yet."
              className={styles.empty}
            />
          )}
        </Card>

        {isApproved && (
          <Card className={styles.actionCard}>
            <div className={styles.actionHeader}>
              <div className={styles.actionHeaderText}>
                <Typography.Title level={4} className={styles.cardTitle}>
                  Acceptance Form
                </Typography.Title>

                <Typography.Paragraph className={styles.cardText}>
                  Your scholarship application has been approved. Download the
                  acceptance template, fill it out, then upload the completed
                  form to confirm your acceptance.
                </Typography.Paragraph>
              </div>
            </div>

            <div className={styles.actions}>
              <Button
                type="button"
                text="Download Acceptance Form"
                isLoading={isDownloadingAcceptanceTemplate}
                disabled={!isFormsUnlocked || isDownloadingAcceptanceTemplate}
                onClick={downloadAcceptanceTemplate}
                className={styles.actionButton}
              />
              <input
                ref={acceptanceFileInputRef}
                type="file"
                className={styles.hiddenFileInput}
                onChange={handleAcceptanceFileChange}
                disabled={!isFormsUnlocked || isUploadingAcceptanceForm}
              />
              <Button
                type="button"
                text="Upload Acceptance Form"
                isLoading={isUploadingAcceptanceForm}
                disabled={!isFormsUnlocked || isUploadingAcceptanceForm}
                onClick={handleAcceptanceUploadClick}
                className={styles.actionButton}
                bgColor="#CE9938"
              />
            </div>

            {selectedAcceptanceFileName && (
              <Alert
                type="success"
                showIcon
                message="Acceptance form received"
                description={`${selectedAcceptanceFileName} has been submitted.`}
                className={styles.alert}
              />
            )}
          </Card>
        )}
      </div>
    </PageLayout>
  );
};

export default Scholarship;
